import {announce} from '@github-ui/aria-live'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- Type-only; no Relay code enters this hook.
import type {IssueDeletionConnection} from '@github-ui/issue-viewer/OptionConfig'
import {
  parentSubIssueDeletionConnection,
  // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- This helper has no Relay dependency.
} from '@github-ui/issue-viewer/ParentSubIssueDeletionConnection'
import {
  issueViewerSecondaryViewQueryOptions,
  // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- Query options factory; no Relay code enters this hook.
} from '@github-ui/issue-viewer/IssueViewerSecondaryViewQuery'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- Mutation state has no Relay dependency.
import {useIssueDeletionState} from '@github-ui/issue-viewer/useIssueDeletionState'
import {
  parseSidePanelIssue,
  type SidePanelIssue,
  toSidePanelIssue,
  writeSidePanelIssueToUrl,
} from '@github-ui/issues-shared/side-panel-url'
import {useQuery} from '@github-ui/react-query'
import type {DashboardIssueIdentifier} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
import {useAnalytics} from '@github-ui/use-analytics'
import {startTransition, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {
  findParentListTargetForIssue,
  focusParentListTarget,
  resolveParentListTarget,
} from './issue-viewer-side-panel-focus'

const DELETE_REJECTED_MESSAGE = 'Could not delete the issue. The sub-issue was restored. Please try again.'
const DELETE_COMPLETION_MESSAGE =
  'The issue was deleted, but this page could not be refreshed. Reload the page to recover.'

type SidePanelPane = {
  instance: object
  issue: SidePanelIssue
  deletionConnection?: IssueDeletionConnection
  parentListTarget: HTMLElement | null
}

type DeletionErrorNotice = {
  title: string
  message: string
}

function issueKey({owner, repo, number}: DashboardIssueIdentifier): string {
  return `${owner}/${repo}#${number}`
}

export function useIssueViewerSidePanel({
  parentOwner,
  parentRepo,
  parentNumber,
}: {
  parentOwner?: string
  parentRepo?: string
  parentNumber?: number
}) {
  const deletions = useIssueDeletionState()
  const {sendAnalyticsEvent} = useAnalytics()
  // Reads cached data for the parent issue (the page you're on), so deleting from the panel knows
  // which sub-issue list to drop a row from. The panel's own issue loads its data separately.
  //
  // Read-only, but every reader of a query key overwrites its settings — so these must be the
  // page's real settings, or the fetch function gets stripped and refreshes silently break.
  const parentSecondaryViewOptions = useMemo(
    () =>
      issueViewerSecondaryViewQueryOptions({
        owner: parentOwner ?? '',
        repo: parentRepo ?? '',
        number: parentNumber ?? 0,
        // Tells the server to clear the issue's notification. This hook never fetches, but a
        // background refresh can pick up these settings, so it has to say the same thing the
        // viewer says.
        markAsRead: true,
        sendAnalyticsEvent,
      }),
    [parentNumber, parentOwner, parentRepo, sendAnalyticsEvent],
  )
  const parentSecondaryView = useQuery({...parentSecondaryViewOptions, enabled: false})

  // A new instance on every open prevents an old deletion from closing a reopened pane.
  const [pane, setPane] = useState<SidePanelPane | null>(null)
  const currentPaneRef = useRef<SidePanelPane | null>(null)
  const deletionPhasesRef = useRef(new Map<number, 'pending' | 'done'>())
  const retainedDeletionConnectionsRef = useRef(new Map<string, IssueDeletionConnection>())
  const panelReturnFocusRef = useRef<HTMLElement | null>(null)
  const pendingFocusTargetRef = useRef<HTMLElement | null>(null)
  const [deletionErrorNotice, setDeletionErrorNotice] = useState<DeletionErrorNotice | null>(null)

  const commitPane = useCallback((next: SidePanelPane | null, focusTarget: HTMLElement | null = null) => {
    pendingFocusTargetRef.current = focusTarget
    currentPaneRef.current = next
    if (next) {
      startTransition(() => setPane(next))
    } else {
      setPane(null)
    }
    writeSidePanelIssueToUrl(next?.issue ?? null)
  }, [])

  const resolveDeletionConnection = useCallback(
    (issue: SidePanelIssue) => {
      if (!issue.owner || !issue.repo) return undefined
      const identifier = {owner: issue.owner, repo: issue.repo, number: issue.number}
      return (
        retainedDeletionConnectionsRef.current.get(issueKey(identifier)) ??
        (parentOwner && parentRepo && parentNumber
          ? parentSubIssueDeletionConnection(
              {owner: parentOwner, repo: parentRepo, number: parentNumber},
              identifier,
              parentSecondaryView.data,
            )
          : undefined)
      )
    },
    [parentNumber, parentOwner, parentRepo, parentSecondaryView.data],
  )

  const showPanelIssue = useCallback(
    (issue: SidePanelIssue, deletionConnection?: IssueDeletionConnection) => {
      const clickedElement = document.activeElement as HTMLElement | null
      const previousPane = currentPaneRef.current
      const drillingInsidePanel = Boolean(previousPane && clickedElement?.closest('#issue-viewer-side-panel'))
      if (!drillingInsidePanel) panelReturnFocusRef.current = clickedElement
      // A nested row disappears with the panel; keep its surviving parent list for focus.
      const parentListTarget = drillingInsidePanel
        ? (previousPane?.parentListTarget ?? null)
        : resolveParentListTarget(clickedElement)
      if (deletionConnection && issue.owner && issue.repo) {
        retainedDeletionConnectionsRef.current.set(
          issueKey({owner: issue.owner, repo: issue.repo, number: issue.number}),
          deletionConnection,
        )
      }
      commitPane({
        instance: {},
        issue,
        deletionConnection: deletionConnection ?? resolveDeletionConnection(issue),
        parentListTarget: parentListTarget ?? findParentListTargetForIssue(issue),
      })
    },
    [commitPane, resolveDeletionConnection],
  )
  const closePanel = useCallback(() => commitPane(null), [commitPane])
  const showSubIssue = useCallback(
    (issue: DashboardIssueIdentifier, deletionConnection?: IssueDeletionConnection) =>
      showPanelIssue(toSidePanelIssue(issue), deletionConnection),
    [showPanelIssue],
  )

  useEffect(() => {
    const currentPane = currentPaneRef.current
    if (!currentPane || currentPane.deletionConnection) return
    const deletionConnection = resolveDeletionConnection(currentPane.issue)
    if (!deletionConnection) return
    const nextPane = {
      ...currentPane,
      deletionConnection,
      parentListTarget: currentPane.parentListTarget ?? findParentListTargetForIssue(currentPane.issue),
    }
    currentPaneRef.current = nextPane
    setPane(current => (current?.instance === currentPane.instance ? nextPane : current))
  }, [resolveDeletionConnection])

  // The host survives the dialog and panel, so it owns both immediate closure and late errors.
  useEffect(() => {
    for (const deletion of deletions) {
      const phase = deletionPhasesRef.current.get(deletion.id)
      if (!phase) {
        const openPane = currentPaneRef.current
        if (
          !openPane ||
          openPane.issue.owner !== deletion.owner ||
          openPane.issue.repo !== deletion.repository ||
          openPane.issue.number !== deletion.number
        ) {
          continue
        }
        deletionPhasesRef.current.set(deletion.id, 'pending')
        setDeletionErrorNotice(null)
        commitPane(null, openPane.parentListTarget ?? findParentListTargetForIssue(openPane.issue))
      }
      if (deletion.status === 'pending' || phase === 'done') continue
      deletionPhasesRef.current.set(deletion.id, 'done')
      const message =
        deletion.status === 'error'
          ? DELETE_REJECTED_MESSAGE
          : deletion.completionError
            ? DELETE_COMPLETION_MESSAGE
            : null
      if (!message) continue
      setDeletionErrorNotice({
        title: deletion.status === 'error' ? 'Issue deletion failed' : 'Issue deleted; refresh failed',
        message,
      })
      announce(message, {assertive: true})
    }
  }, [commitPane, deletions])

  useEffect(() => {
    if (pane || currentPaneRef.current) return
    const target = pendingFocusTargetRef.current
    pendingFocusTargetRef.current = null
    return focusParentListTarget(target)
  }, [pane])

  // Hydrate from the URL only after mount; the panel's lazy Relay chunk must not enter SSR.
  useEffect(() => {
    const syncFromUrl = () => {
      const issue = parseSidePanelIssue()
      if (!issue) {
        if (!currentPaneRef.current) return
        currentPaneRef.current = null
        startTransition(() => setPane(null))
        return
      }

      const next = {
        instance: {},
        issue,
        deletionConnection: resolveDeletionConnection(issue),
        parentListTarget: findParentListTargetForIssue(issue),
      }
      panelReturnFocusRef.current = document.activeElement as HTMLElement | null
      pendingFocusTargetRef.current = null
      currentPaneRef.current = next
      startTransition(() => setPane(next))
    }
    syncFromUrl()
    window.addEventListener('popstate', syncFromUrl)
    return () => {
      currentPaneRef.current = null
      window.removeEventListener('popstate', syncFromUrl)
    }
  }, [resolveDeletionConnection])

  const expandPanel = useCallback(() => {
    currentPaneRef.current = null
    startTransition(() => setPane(null))
  }, [])

  const handleIssueDelete = useCallback(() => {
    if (!pane || pane.deletionConnection || currentPaneRef.current?.instance !== pane.instance) return
    commitPane(null, pane.parentListTarget ?? findParentListTargetForIssue(pane.issue))
  }, [commitPane, pane])

  return {
    showSubIssue,
    deletionErrorNotice,
    sidePanelProps: {
      issue: pane?.issue ?? null,
      onClose: closePanel,
      onExpand: expandPanel,
      onIssueDelete: handleIssueDelete,
      hostReportsIssueDeletionErrors: true,
      issueDeletionConnection: pane?.deletionConnection,
      onSubIssueSelect: showSubIssue,
      returnFocusRef: panelReturnFocusRef,
    },
  }
}
